import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@fortawesome/free-solid-svg-icons";
import { getRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";
import moment from "moment";

import MainA from "../../layout/Main Admin";
import Button from "../../components/atoms/Button";
import Loader from "../../components/atoms/Loader";
import Table from "../../components/atoms/Table";
import CustomDropdown from "../../components/atoms/CustomDropdown";
import Input from "../../components/atoms/Input";

import "./styles.scss";

const CleanDataRequest = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState([]);
  const [sortColumn, setSortColumn] = useState("requestType");
  const [sortOrder, setSortOrder] = useState("asc");
  const [eventData, setEventData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState({
    label: "All Events",
    value: "",
  });
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchText, setSearchText] = useState("");


  useEffect(() => {
    fetchRequests();
    getEvents();
  }, []);


  const fetchRequests = async () => {
    try {
      setLoading(true);

      const requestResp = await getRequest("CleanDataRequest/GetAll");

      setRequestData(requestResp.data || []);
      setFilteredRequests(requestResp.data || []);
    } catch (error) {
      console.error(error);
      NotificationManager.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const getEvents = async () => {
    try {
      const resp = await getRequest(
        `Page/GetPagesByCategoryType/${categoryId}`
      );
      setEventData(resp.data);
    } catch (error) {
      console.error("Error fetching event:", error);
    }
  };

  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    setSortColumn(column);
    setSortOrder(order);

    const sorted = [...requestData].sort((a, b) => {
      const aVal = a[column] || "";
      const bVal = b[column] || "";
      return order === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    setRequestData(sorted);
  };

  const handleEventFilter = (eventId) => {
    const selected =
      eventId
        ? {
            label:
              eventData.find((e) => String(e.pageId) === String(eventId))
                ?.pageName || "Event",
            value: eventId,
          }
        : { label: "All Events", value: "" };

    setSelectedEvent(selected);
    applyFilters(eventId, searchText);
  };


  const applyFilters = (eventId, text) => {
    let data = [...requestData];

    if (eventId) {
      data = data.filter(
        (item) => String(item.pageId) === String(eventId)
      );
    }

    if (text) {
      const lower = text.toLowerCase();

      data = data.filter((item) => {
        const eventName =
          eventData.find((e) => e.pageId === item.pageId)?.pageName || "";

        return (
          item.status?.toLowerCase().includes(lower) ||
          item.requestType?.toLowerCase().includes(lower) ||
          eventName.toLowerCase().includes(lower)
        );
      });
    }

    setFilteredRequests(data);
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    applyFilters(selectedEvent.value, value);
  };

  const requestType = {
    "UnProcessedImages": "Unprocessed Images",
    "ProcessedImages": "Processed Images by BIB",
    "ProcessedReels": "Processed Reels",
    "ProcessedImagesByFace": "Processed Images by Face",
  };

  return (
    <MainA className="clean-data-request">
      {loading && <Loader loading={loading} />}

      <div className="gallery-wrapper">
        <div className="event-header">
          <h1>Clean Data Requests</h1>

          <div className="filter">
            <Button
              title="Refresh"
              onClick={fetchRequests}
              className="refresh-btn"
            >
              <FontAwesomeIcon icon={faRefresh} />
            </Button>
            <Input
              className="search-input"
              type="search"
              placeholder="Search"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <CustomDropdown
              options={[
                { label: "All Events", value: "" },
                ...eventData.map((event) => ({
                  label: event.pageName,
                  value: event.pageId,
                })),
              ]}
              className="event-dropdown"
              onSelect={(option) => handleEventFilter(option.value)}
              value={selectedEvent}
            />

            <Button
              onClick={() => navigate(-1)}
              className="back-btn"
              title="Back"
            >
              Back
            </Button>
          </div>
        </div>
        <div className="requests-table">
          <Table
            pagination={true}
            pageSize={10}
            totalRows={filteredRequests.length}
            categoryName="clean-data-requests"
          >
            <thead>
              <tr>
                <th onClick={() => handleSort("pageId")}>Event</th>
                <th onClick={() => handleSort("requestType")}>Request Type</th>
                <th style={{ textAlign: "center" }}>Files</th>
                <th style={{ textAlign: "center" }}>Size</th>
                <th onClick={() => handleSort("createdAt")}>Cleaned At</th>
                <th onClick={() => handleSort("status")}>Status</th>
              </tr>
            </thead>

            <tbody>
              {filteredRequests.length ? (
                filteredRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((item) => (
                  <tr key={item.cleanDataRequestId}>
                    <td>
                      <a
                        href={`/events/clips/${item.pageId}?categoryId=${categoryId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {
                          eventData.find((event) => event.pageId === item.pageId)
                            ?.pageName
                        }
                      </a>
                    </td>
                    <td>{requestType[item.requestType]}</td>
                    <td style={{ textAlign: "center" }}>{item.totalFiles ?? "—"}</td>
                    <td style={{ textAlign: "center" }}>{item.folderSize ?? "—"}</td>
                    <td>
                      {item.createdAt
                        ? moment(item.createdAt).format("DD MMM YYYY, hh:mm A")
                        : "—"}
                    </td>
                    <td>
                      <span className={`status ${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No requests found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </MainA>
  );
};

export default CleanDataRequest;
